import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFeature } from '../featureFlags/FeatureGate';
import { FEATURE_TMS_EXPORT_ONE_STEP, viaWriteStateName } from '../../shared/status';
import { fmtDateTimeShort } from '../../shared/listCells';
import { MARGIN_BAND_LABELS, marginBandColor } from '../userOffers/carrierOffersApi';
import {
  FEATURE_ADMIN_DASHBOARD_SECTIONS,
  fetchAdminDashboardSections,
  money,
  type AdminDashboardSectionsData,
  type SoldRow,
  type TmsWriteRow,
} from '../settlement/settlementApi';

/**
 * Sekcje pulpitu Administracji pod kafelkami (prace 26.10.2026, pakiet P4b;
 * makieta p. Mariusza „Pulpit Administracji”; przełącznik
 * `adminDashboardSections`, S-26.10-11): „Ostatnie zapisy do TMS” z numerem
 * w TMS z importu i „Sprzedane przez Trade” z marżą. Tylko odczyt — liczby
 * i powody braków z API.
 */

export const SECTIONS_LOAD_ERROR = 'Nie udało się wczytać sekcji pulpitu Administracji. Odśwież stronę.';

const OUTCOME_COLOR: Record<TmsWriteRow['outcome'], 'success' | 'info' | 'error' | 'default'> = {
  saved: 'success',
  sent: 'info',
  failed: 'error',
  pending: 'default',
  other: 'default',
};

const TmsWritesBox = ({ rows, tmsNames }: { rows: TmsWriteRow[]; tmsNames: boolean }) => (
  <Card variant="outlined" data-testid="adm-dashboard-tms-writes">
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Ostatnie zapisy do TMS
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nie było jeszcze zapisu do TMS.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Zlecenie</TableCell>
                <TableCell>Numer w TMS</TableCell>
                <TableCell>Wynik</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.orderId} data-testid="adm-dashboard-tms-write-row">
                  <TableCell>
                    <Link to={`/orders/${r.orderId}/show`}>{r.orderNumber ?? `#${r.orderId}`}</Link>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {[r.client, r.customerOrderNumber].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.tmsNumber ?? (
                      <Typography variant="caption" color="text.secondary">
                        jeszcze nie ma — przyjdzie z importu
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={OUTCOME_COLOR[r.outcome]}
                      label={viaWriteStateName(r.viaWriteStatus, tmsNames)}
                    />
                    {r.retried ? (
                      <Chip size="small" variant="outlined" sx={{ ml: 0.5 }} label="po ponowieniu" />
                    ) : null}
                    <Typography variant="caption" color="text.secondary" component="div">
                      {r.lastChangeAt ? fmtDateTimeShort(r.lastChangeAt) : ''}
                      {r.attempts > 0 ? ` · prób dostarczenia: ${r.attempts}` : ''}
                      {r.lastError ? ` · ${r.lastError}` : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </CardContent>
  </Card>
);

const MarginCell = ({ r }: { r: SoldRow }) => {
  if (r.marginPln === null && r.marginInOrderCurrency === null) {
    return (
      <Tooltip title={r.missing.join('; ')}>
        <Typography variant="body2" color="text.secondary" data-testid="adm-dashboard-sold-margin-missing">
          brak: {r.missing[0] ?? 'dane'}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Box>
      <Typography variant="body2">
        {r.marginInOrderCurrency
          ? money(r.marginInOrderCurrency.amount, r.marginInOrderCurrency.currency)
          : money(r.marginPln, 'PLN')}
        {r.marginInOrderCurrency && r.marginPln !== null ? ` (${money(r.marginPln, 'PLN')})` : ''}
      </Typography>
      {r.band ? (
        <Tooltip title={r.bandRule ?? ''}>
          <Chip size="small" color={marginBandColor(r.band)} label={MARGIN_BAND_LABELS[r.band] ?? r.band} />
        </Tooltip>
      ) : null}
    </Box>
  );
};

const SoldBox = ({ rows, windowDays }: { rows: SoldRow[]; windowDays: number }) => (
  <Card variant="outlined" data-testid="adm-dashboard-sold">
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Sprzedane przez Trade · do rozliczenia marży
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zleceń sprzedanych przez Trade w ostatnich {windowDays} dniach.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Zlecenie</TableCell>
                <TableCell>Przewoźnik</TableCell>
                <TableCell align="right">Cena klienta</TableCell>
                <TableCell align="right">Koszt przewoźnika</TableCell>
                <TableCell>Marża</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.userOfferId} data-testid="adm-dashboard-sold-row">
                  <TableCell>
                    <Link to={`/user-offers/${r.userOfferId}/show`}>{r.number ?? `#${r.userOfferId}`}</Link>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {r.client ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.carrier ?? '—'}
                    <Typography variant="caption" color="text.secondary" component="div">
                      {r.vehicle ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{money(r.clientPrice, r.clientCurrency)}</TableCell>
                  <TableCell align="right">
                    {r.cost ? money(r.cost.amount, r.cost.currency) : '—'}
                    {r.costSource ? (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {r.costSource}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <MarginCell r={r} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </CardContent>
  </Card>
);

export const AdminDashboardSections = () => {
  const on = useFeature(FEATURE_ADMIN_DASHBOARD_SECTIONS);
  const tmsNames = useFeature(FEATURE_TMS_EXPORT_ONE_STEP);
  const [data, setData] = useState<AdminDashboardSectionsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!on) return undefined;
    let active = true;
    fetchAdminDashboardSections()
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(SECTIONS_LOAD_ERROR);
      });
    return () => {
      active = false;
    };
  }, [on]);

  if (!on) return null;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;
  return (
    <Box
      data-testid="adm-dashboard-sections"
      sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) minmax(0,1fr)' } }}
    >
      <TmsWritesBox rows={data.tmsWrites} tmsNames={tmsNames} />
      <SoldBox rows={data.sold} windowDays={data.soldWindowDays} />
    </Box>
  );
};
